import ModelBuilder from 'helpers/ModelBuilder';
import { EntityBase, EntityLike } from './EntityBase';
import { DateRangeInfo, VisitorsInfo } from 'interfaces/ReservationSubTypes';
import { ReservationPayload } from 'services/interfaces/ReservationPayload';
import dayjs from 'dayjs';

export class Reservation extends EntityBase {
  id: string;
  siteId: string;
  siteTypeId: string;
  guestId: string;
  guestName: string;
  dateRange: DateRangeInfo;
  visitorsInfo: VisitorsInfo;
  vehicleType: string;
  vehicleLengthRange: string;
  vehicleSlides: string[];
  vehicleTowing: string;
  vehicleElectrical: string[];
  rateId: string;
  rateOverride: number;
  discountId: string;
  discountCode: string;
  loyaltyNumber: string;
  paymentMethodId: string;
  notes: string;

  static Model: typeof ModelDefinition;

  static new(object: EntityLike<Reservation>) {
    return super.fill(this, object);
  }

  static toJSON = (model: Reservation) => {
    const [arrival, departure] = model.dateRange || [];
    const plainObj = { ...model,
      dateRange: undefined,
      arrival: arrival?.format('YYYY-MM-DD'),
      departure: departure?.format('YYYY-MM-DD'),
    };
    const result = JSON.stringify(plainObj);
    return result;
  }

  static fromJSON = (jsonObj: string) => {
    const plainObj = JSON.parse(jsonObj);
    const model = Reservation.new(plainObj);
    if (plainObj.arrival && plainObj.departure) {
      const arrivalEpoch = Math.max(+dayjs(plainObj.arrival), +dayjs());
      const departureEpoch = Math.max(+dayjs(plainObj.departure), +dayjs());
      if (departureEpoch > arrivalEpoch) {
        model.dateRange = [dayjs(arrivalEpoch), dayjs(departureEpoch)];
      }
    }
    return model;
  }
}

let fields = ModelBuilder.buildFormFields<Reservation>({
  id: null,
  guestId: null,
  paymentMethodId: null,
  siteId: { label: 'Site #' },
  siteTypeId: { label: 'Site Type' },
  guestName: { label: 'Reservation Guest' },
  dateRange: { label: 'Dates' },
  visitorsInfo: { label: 'Guests' },
  vehicleType: { label: 'RV Type', },
  vehicleLengthRange: { label: 'RV Size' },
  vehicleSlides: { label: 'RV Slide', type: 'multiple' },
  vehicleTowing: { label: 'Towing' },
  vehicleElectrical: { label: 'Electrical Service', type: 'multiple' },
  rateId: { label: 'Rate Type' },
  rateOverride: { label: 'Rate Override', placeholder: '-.--', type: 'numeric' },
  discountId: { label: 'Discount' },
  discountCode: { label: 'Discount Code', placeholder: 'Code' },
  loyaltyNumber: { label: 'Discount Loyalty', placeholder: 'Loyalty' },

  notes: { label: 'Additional Notes', type: 'textarea' },
})

fields = ModelBuilder.setRulesToFields(fields, {
  dateRange: [{ required: true, message: 'Reservation dates required' }],
  visitorsInfo: [{ required: true }],
  siteTypeId: [{ required: true }],
  vehicleType: [{ required: true }],
  vehicleLengthRange: [{ required: true }],
});

const buildPayload = (model: Reservation, parkId: string) => {
  const [arrival, departure] = model.dateRange;
  const payload = ReservationPayload.new({...model, ...model.visitorsInfo});
  payload.parkId = parkId;
  payload.guestId = model.guestId;
  payload.paymentMethodId = model.paymentMethodId;
  payload.arrivalDate = arrival?.format('YYYY-MM-DD');
  payload.departureDate = departure?.format('YYYY-MM-DD');
  payload.type = 'Reservation';
  payload.action = 'confirm';

  return payload;
}

const initialValues: Partial<Reservation> = {
  visitorsInfo: { adults: 2, children: 0, pets: 0 }
}

const isLongTerm = (model: Reservation) => {
  if (!model.dateRange) return false;

  const [start, end] = model.dateRange;
  const diff = end.diff(start, 'days');
  return diff > 30;
}

class ModelDefinition {
  static fields = fields;
  static isLongTerm = isLongTerm;
  static buildPayload = buildPayload;
  static initialValues = initialValues;
}
Reservation.Model = ModelDefinition;
